import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminPatrolSystemMapping } from "../../../../../http/patrol/types/patrolSystemMappings";
import { usePatrolSystemMappingsApi } from "../../../../../http/patrol/usePatrolSystemMappings";
import { PaginationQuery } from "../../../../../types";
import { formatBoolean } from "../../../../../utils";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import CreateSystemMappingModalDialogButton from "./CreateSystemMappingModalDialogButton";
import SystemMappingForm from "./SystemMappingForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const SystemMappings: FC<Props> = ({ active }) => {
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "systemMappings" });
  const aborter = useRef(new AbortController());
  const patrolSystemMappingsApi = usePatrolSystemMappingsApi();
  const { setActions, setTitle } = useSearchContext();

  const [itemToEdit, setItemToEdit] = useState<AdminPatrolSystemMapping>();
  const [itemToDelete, setItemToDelete] = useState<AdminPatrolSystemMapping>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "systemMappings");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = patrolSystemMappingsApi.useAdminPatrolSystemMappings(
    patrolSystemId!,
    query,
    aborter.current.signal,
  );

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.patrolSystemMappings.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.patrolSystemMappings.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateSystemMappingModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminPatrolSystemMapping>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.roofCategories.singular}</th>
            <th align="left">{i18n.translation.roofMaterials.singular}</th>
            <th align="left">{i18n.translation.roofSubstructures.singular}</th>
            <th align="right">{i18n.translation.common.minSubstructureHeight}</th>
            <th align="right">{i18n.translation.common.minSubstructureSize}</th>
            <th align="right">{i18n.translation.common.minMetalPlateThickness}</th>
            <th align="left">{i18n.translation.common.roofWeightRequired}</th>
            <th align="left">{i18n.translation.common.seamsSpanRequired}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>{item.roofCategory.name}</td>
            <td>{item.roofMaterial?.name}</td>
            <td>{item.roofSubstructure?.name}</td>
            <td align="right">{item.minSubstructureHeight ? `${item.minSubstructureHeight} mm` : undefined}</td>
            <td align="right">
              {item.minSubstructureSize
                ? `${item.minSubstructureSize.height}x${item.minSubstructureSize.width} mm`
                : undefined}
            </td>
            <td align="right">{item.minMetalPlateThickness ? `${item.minMetalPlateThickness} mm` : undefined}</td>
            <td>{formatBoolean(i18n.translation, item.roofWeightRequired)}</td>
            <td>{formatBoolean(i18n.translation, item.seamsSpanRequired)}</td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.patrolSystemMappings.edit}>
          <FlexContent direction="row">
            <SystemMappingForm systemMapping={itemToEdit} onClose={() => setItemToEdit(undefined)} />
          </FlexContent>
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.patrolSystemMappings.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                patrolSystemMappingsApi
                  .deleteAdminPatrolSystemMapping(patrolSystemId!, itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.patrolSystemMappings.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default SystemMappings;
